// frontend/src/ui/AccessoryModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, SelectInput, TextInput } from "./UIComponents";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const isTextInput = (s) => String(s || "").trim().toLowerCase() === "text input var char";
const isDigitInput = (s) => String(s || "").trim().toLowerCase() === "text input digit";
const splitChoices = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.length > 0);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function AccessoryModal({
  open,
  onClose,
  prod_id,
  prod_label,
  operation_id,
  operation_label,
  selectedOpIds,
}) {
  // --- State for Ops Config (SQL #3 equivalent) ---
  const [rowsOps, setRowsOps] = useState([]);
  const [valuesOps, setValuesOps] = useState({});
  const [loadingOps, setLoadingOps] = useState(false);
  const [errorOps, setErrorOps] = useState("");

  // --- Derived ---
  const displayOpLabel = useMemo(() => {
    return String(operation_label || "").split(",")[0].trim();
  }, [operation_label]);

  const opIdsForQuery = useMemo(() => {
    const arr = Array.isArray(selectedOpIds)
      ? selectedOpIds
      : operation_id
      ? [operation_id]
      : [];
    return arr.map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0);
  }, [selectedOpIds, operation_id]);

  // --- Reset on Close ---
  useEffect(() => {
    if (!open) {
      setValuesOps({});
      setRowsOps([]);
      setErrorOps("");
    }
  }, [open]);

  // --- Fetch Ops Config (SQL #3) ---
  useEffect(() => {
    let ignore = false;
    async function loadOpsConfig() {
      if (!Number(prod_id) || opIdsForQuery.length === 0) {
        if (!ignore) {
          setRowsOps([]);
          setValuesOps({});
        }
        return;
      }

      setLoadingOps(true);
      setErrorOps("");

      try {
        const params = new URLSearchParams({
          action: "ops_config",
          prod_id: String(prod_id),
          op_ids: JSON.stringify(opIdsForQuery),
        });
        const url = `/api/default-product-config?${params.toString()}`;
        const resp = await fetch(url, { method: "GET" });
        const data = await resp.json();

        if (!resp.ok || data?.ok === false) {
          throw new Error(data?.error || `HTTP ${resp.status}`);
        }
        if (ignore) return;

        const items = Array.isArray(data.items) ? data.items : [];
        setRowsOps(items);

        // Seed values
        const seed = {};
        for (const row of items) {
          const id = String(row.config_id);
          const opts = String(row.options || "");
          const def = row.def_value != null ? String(row.def_value) : "";

          if (isTextInput(opts) || isDigitInput(opts)) {
            seed[id] = def || "";
          } else {
            const choices = splitChoices(opts);
            seed[id] = choices.includes(def) ? def : "";
          }
        }
        setValuesOps(seed);
      } catch (err) {
        if (!ignore) setErrorOps(err.message || "Load failed");
      } finally {
        if (!ignore) setLoadingOps(false);
      }
    }

    if (open) loadOpsConfig();

    return () => {
      ignore = true;
    };
  }, [open, prod_id, opIdsForQuery]);

  // --- Handlers ---
  const updateValueOps = (configId, newVal) => {
    setValuesOps((prev) => ({
      ...prev,
      [String(configId)]: newVal,
    }));
  };

  const handleShowCart = () => {
    console.log("AccessoryModal: Show Cart clicked (No Action)");
  };

  const handleAddToCart = () => {
    console.log("AccessoryModal: Add to Cart clicked (No Action)");
  };

  const handleDownload = () => {
    console.log("AccessoryModal: Download clicked (No Action)");
  };

  const handleContinue = () => {
    console.log("AccessoryModal: Continue clicked (No Action)");
  };

  // --- Render Field Helper ---
  const renderFieldOps = (r) => {
    const idStr = String(r.config_id);
    const label = r.config || `Config ${idStr}`;
    const opts = String(r.options || "");
    const v = valuesOps[idStr] ?? "";

    if (isTextInput(opts) || isDigitInput(opts)) {
      return (
        <div key={idStr}>
          <TextInput
            label={label}
            value={v}
            onChange={(e) => updateValueOps(idStr, e.target.value)}
          />
        </div>
      );
    }

    const choices = splitChoices(opts);
    return (
      <div key={idStr}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
          {label}
        </label>
        <SelectInput
          value={v}
          onChange={(e) => updateValueOps(idStr, e.target.value)}
          choices={choices}
          style={{ width: "100%" }}
        />
      </div>
    );
  };

  // --- Footer ---
  const footerView = (
    <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <Button
          onClick={handleShowCart}
          style={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
        >
          Xem giỏ / Show Cart
        </Button>
      </div>
      <div style={{ display: "flex", gap: "8px" }}>
        <Button onClick={handleDownload} disabled={loadingOps}>
          Tải về cấu hình / Download config
        </Button>
        <Button onClick={handleContinue} disabled={loadingOps}>
          Tiếp tục / Continue
        </Button>
      </div>
    </div>
  );

  // --- Main Render ---
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cấu hình Phụ kiện"
      maxWidth={720}
      footer={footerView}
    >
      <div style={{ display: "grid", gap: 10 }}>
        {/* Summary Box (Modified: No W/H, No Max M2) */}
        <div className="box" style={{ margin: 0, position: "relative" }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Tóm tắt lựa chọn / Selection summary
          </div>
          <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
            <div>
              Dòng sản phẩm / Product line:{" "}
              <b title={`ID: ${prod_id || "-"}`}>
                {prod_label || `(ID: ${prod_id || "-"})`}
              </b>
            </div>
            <div>
              Vận hành / Operation:{" "}
              <b title={`ID: ${operation_id || "-"}`}>
                {displayOpLabel || `(ID: ${operation_id || "-"})`}
              </b>
            </div>
          </div>
        </div>

        {/* Operation-Specific Config Only */}
        <div className="box" style={{ margin: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Cấu hình tùy chọn / Optional configuration
          </div>
          {loadingOps ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Đang tải...</div>
          ) : errorOps ? (
            <div style={{ color: "crimson", fontSize: 13 }}>Lỗi: {errorOps}</div>
          ) : rowsOps.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              Không có cấu hình tùy chọn / No optional configuration.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {rowsOps.map(renderFieldOps)}
            </div>
          )}
        </div>

        {/* Add to Cart Button (Restored) */}
        <Button
          onClick={handleAddToCart}
          disabled={loadingOps}
          style={loadingOps ? { opacity: 0.7, cursor: "wait" } : {}}
        >
          {loadingOps ? "Đang xử lý..." : "Thêm vào giỏ / Add to cart"}
        </Button>
      </div>
    </Modal>
  );
}